import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChildFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import Swal from 'sweetalert2';
import { AuthService } from '../../auth/services/auth.service';
import { AdminPermisosService } from '../services/admin-permisos.service';

/**
 * Verifica que la ruta del admin esté en el menú del usuario (ver AdminPermisosService).
 *
 * - Sin sesión: no decide nada (devuelve false) y deja que `AuthGuard`, que va antes en la lista,
 *   haga la redirección a /iniciar-sesion.
 * - Sin ninguna página del admin en su menú (usuario normal): a /inicio.
 * - Con menú, pero sin esa ruta: a /inicio-administrador con un aviso.
 *
 * Excepciones, declaradas en la ruta:
 * - `data: { sinPermiso: true }`: cualquiera con acceso al admin puede entrar (ej. /inicio-administrador).
 * - `data: { permiso: '/modulo' }`: rutas que no están en el menú (detalle, edición) — basta con
 *   tener alguna página de ese módulo.
 */
const verificarPermiso = (state: RouterStateSnapshot): Observable<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const permisosService = inject(AdminPermisosService);
  const router = inject(Router);

  return authService.validateAuth().pipe(
    switchMap(autenticado => {
      const userId = authService.getUser()?.id;
      if (!autenticado || !userId) return of(false);

      return permisosService.getUrlsPermitidas(userId).pipe(
        map(urls => {
          if (urls.length === 0) {
            avisar('No tienes acceso al panel de administrador.');
            return router.parseUrl('/inicio');
          }

          const { sinPermiso, permiso } = leerData(state.root);
          if (sinPermiso) return true;

          const ruta = router.parseUrl(state.url).root.children['primary']?.segments.map(s => s.path).join('/') ?? '';
          const permitido = permiso
            ? permisosService.moduloPermitido(permiso, urls)
            : permisosService.rutaPermitida('/' + ruta, urls);

          if (permitido) return true;

          avisar('No tienes permisos para acceder a esta sección.');
          return router.parseUrl('/inicio-administrador');
        })
      );
    }),
    catchError(() => of(router.parseUrl('/inicio')))
  );
};

/** Recorre la ruta activada de la raíz a la hoja; el `data` más profundo gana. */
function leerData(root: ActivatedRouteSnapshot): { sinPermiso: boolean; permiso: string | null } {
  let sinPermiso = false;
  let permiso: string | null = null;
  let nodo: ActivatedRouteSnapshot | null = root;
  while (nodo) {
    if (nodo.routeConfig?.data?.['sinPermiso'] !== undefined) sinPermiso = !!nodo.routeConfig.data['sinPermiso'];
    if (nodo.routeConfig?.data?.['permiso']) permiso = nodo.routeConfig.data['permiso'];
    nodo = nodo.firstChild;
  }
  return { sinPermiso, permiso };
}

function avisar(mensaje: string): void {
  Swal.fire({ icon: 'warning', title: 'Acceso restringido', text: mensaje, confirmButtonText: 'Entendido' });
}

export const adminPermisosChildGuard: CanActivateChildFn = (_childRoute, state) => verificarPermiso(state);
